<script setup lang="ts">
    import remend from 'remend'
    import { marked } from 'marked'
    import markedKatex from 'marked-katex-extension'
    import mermaid from 'mermaid'
    import { computed, nextTick, useTemplateRef, watch } from 'vue'

    const props = defineProps<{
        markdown?: string
        loading?: boolean
    }>()

    mermaid.initialize({ startOnLoad: false })

    marked.use(markedKatex({ throwOnError: false }))

    const renderer = new marked.Renderer()

    // open links in new tab with security attributes
    renderer.link = ({ href, title, text }) => {
        const titleAttr = title ? ` title="${title}"` : ''
        return `<a href="${href}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`
    }

    // wrap mermaid code blocks in a dedicated div for post-render processing
    renderer.code = ({ text }) => {
        if (
            text.match(/^sequenceDiagram/) ||
            text.match(/^graph/) ||
            text.match(/^classDiagram/) ||
            text.match(/^stateDiagram/) ||
            text.match(/^erDiagram/) ||
            text.match(/^gantt/) ||
            text.match(/^pie/) ||
            text.match(/^flowchart/) ||
            text.match(/^journey/)
        ) {
            return '<pre class="mermaid">' + text + '</pre>'
        }
        return '<pre><code>' + text + '</code></pre>'
    }

    const containerRef = useTemplateRef<HTMLDivElement>('container')

    const html = computed(() => {
        // complete the markdown with remend to ensure any incomplete markdown is properly closed before parsing
        const completedMarkdown = remend(props.markdown || '')
        // parse the completed markdown to HTML
        return marked.parse(completedMarkdown, {
            async: false,
            breaks: true,
            renderer,
        }) as string
    })

    async function renderMermaid() {
        await nextTick()
        const nodes =
            containerRef.value?.querySelectorAll<HTMLElement>('.mermaid')
        if (nodes?.length) {
            await mermaid.run({ nodes })
        }
    }
    watch(html, renderMermaid, {
        immediate: true,
    })
</script>

<template>
    <div ref="container" v-html="html"></div>
</template>
